import React from 'react';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, Calculator } from 'lucide-react';

interface WizardStepProps {
  title: string;
  description?: string;
  stepNumber: number;
  totalSteps: number;
  onBack?: () => void;
  onNext?: () => void;
  onSubmit?: () => void;
  isLast?: boolean;
  nextDisabled?: boolean;
  nextDisabledReason?: string;
  children: React.ReactNode;
}

export default function WizardStep({ title, description, stepNumber, totalSteps, onBack, onNext, onSubmit, isLast, nextDisabled, nextDisabledReason, children }: WizardStepProps) {
  return (
    <div className="space-y-6">
      {/* Step header */}
      <div className="border-b pb-4">
        <div className="flex items-center gap-3 mb-1">
          <span className="inline-flex items-center justify-center h-8 w-8 rounded-lg bg-primary text-primary-foreground text-sm font-bold">
            {stepNumber}
          </span>
          <div>
            <h2 className="text-xl font-bold tracking-tight">{title}</h2>
            {description && <p className="text-sm text-muted-foreground">{description}</p>}
          </div>
        </div>
        {/* Progress bar */}
        <div className="mt-4 flex gap-1">
          {Array.from({ length: totalSteps }, (_, i) => (
            <div
              key={i}
              className={`h-1 flex-1 rounded-full transition-colors ${
                i < stepNumber ? 'bg-primary' : i === stepNumber - 1 ? 'bg-primary/40' : 'bg-muted'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Content */}
      <div>{children}</div>

      {/* Step navigation */}
      <div className="flex items-center justify-between pt-4 border-t">
        <div>
          {onBack && (
            <Button variant="outline" size="lg" onClick={onBack}>
              <ChevronLeft className="h-4 w-4 mr-1" /> Back
            </Button>
          )}
        </div>
        <div>
          {isLast ? (
            <Button size="lg" onClick={onSubmit} disabled={nextDisabled} title={nextDisabled ? nextDisabledReason : undefined} className="shadow-lg shadow-primary/20 px-8">
              <Calculator className="h-4 w-4 mr-2" /> Save & Calculate
            </Button>
          ) : (
            <Button size="lg" onClick={onNext} disabled={nextDisabled} title={nextDisabled ? nextDisabledReason : undefined} className="px-8" data-tour="wizard-next">
              Next <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
